import React from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import FilterListIcon from "@mui/icons-material/FilterList";

export default function FilterSortTable({ data = [], columns = [] }) {
  const [sortColumn, setSortColumn] = React.useState(null);
  const [sortAsc, setSortAsc] = React.useState(true);

  // filter: { [columnId]: Set of allowed values }
  const [filter, setFilter] = React.useState({});

  // For filter menu UI
  const [anchorEls, setAnchorEls] = React.useState({});

  const handleSort = (column) => {
    if (sortColumn === column) {
      if (sortAsc) {
        setSortAsc(false);
      } else {
        setSortColumn(null);
      }
    } else {
      setSortColumn(column);
      setSortAsc(true);
    }
  };

  // Open filter menu for a column
  const handleFilterIconClick = (event, columnId) => {
    setAnchorEls((prev) => ({
      ...prev,
      [columnId]: event.currentTarget,
    }));
  };

  // Close filter menu for a column
  const handleFilterMenuClose = (columnId) => {
    setAnchorEls((prev) => ({
      ...prev,
      [columnId]: null,
    }));
  };

  // Toggle value in filter for a column
  const handleFilterValueToggle = (columnId, value) => {
    setFilter((prev) => {
      const prevSet = prev[columnId] || new Set();
      const newSet = new Set(prevSet);
      if (newSet.has(value)) {
        newSet.delete(value);
      } else {
        newSet.add(value);
      }
      // If all values are selected or none, remove filter for that column
      if (newSet.size === 0) {
        const { [columnId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [columnId]: newSet };
    });
  };

  // Get unique values for a column
  const getUniqueValues = (columnId) => {
    const values = data.map((row) => row[columnId]);
    // Remove undefined/null and deduplicate
    return Array.from(new Set(values));
  };

  // Filtering logic: only show rows where each filtered column's value is in the allowed set
  const filteredData = data.filter((row) => {
    return Object.keys(filter).every((columnId) => {
      if (!filter[columnId] || filter[columnId].size === 0) return true;
      return filter[columnId].has(row[columnId]);
    });
  });

  const sortedData = [...filteredData].sort((a, b) => {
    if (sortColumn === null) return 0;
    let aValue = a[sortColumn] || "";
    let bValue = b[sortColumn] || "";

    // String comparison
    if (typeof aValue === "string" && typeof bValue === "string") {
      return sortAsc
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    // Fallback to default comparison
    if (aValue < bValue) return sortAsc ? -1 : 1;
    if (aValue > bValue) return sortAsc ? 1 : -1;
    return 0;
  });

  return (
    <>
      <Typography
        variant="h6"
        color="secondary.light"
        align="right"
        gutterBottom
        sx={{
          width: "98vw",
          marginLeft: "calc((-98vw + 100%) / 2)",
          marginRight: "calc((-98vw + 100%) / 2)",
        }}
      >
        {`Row Count: ${sortedData.length}`}
      </Typography>
      <TableContainer
        component={Paper}
        sx={{
          marginBottom: 4,
          width: "98vw",
          marginLeft: "calc((-98vw + 100%) / 2)",
          marginRight: "calc((-98vw + 100%) / 2)",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column, index) => (
                <TableCell
                  key={index}
                  style={{ cursor: "pointer", verticalAlign: "top" }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                      onClick={() => handleSort(column.id)}
                      style={{ display: "flex", alignItems: "center", flex: 1 }}
                    >
                      <Typography
                        variant="body1"
                        color="secondary.main"
                        display="flex"
                        alignItems="center"
                      >
                        {column.title}
                        {sortColumn === column.id &&
                          (sortAsc ? (
                            <ArrowUpwardIcon
                              fontSize="small"
                              sx={{ ml: 0.5 }}
                            />
                          ) : (
                            <ArrowDownwardIcon
                              fontSize="small"
                              sx={{ ml: 0.5 }}
                            />
                          ))}
                      </Typography>
                    </span>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFilterIconClick(e, column.id);
                      }}
                      sx={{ ml: 0.5 }}
                    >
                      <FilterListIcon
                        color={
                          filter[column.id] && filter[column.id].size > 0
                            ? "primary"
                            : "inherit"
                        }
                        fontSize="small"
                      />
                    </IconButton>
                    <Menu
                      anchorEl={anchorEls[column.id]}
                      open={Boolean(anchorEls[column.id])}
                      onClose={() => handleFilterMenuClose(column.id)}
                      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                      transformOrigin={{ vertical: "top", horizontal: "left" }}
                    >
                      {getUniqueValues(column.id).map((value) => (
                        <MenuItem
                          key={value}
                          onClick={() =>
                            handleFilterValueToggle(column.id, value)
                          }
                          dense
                        >
                          <Checkbox
                            checked={filter[column.id]?.has(value) || false}
                            tabIndex={-1}
                            disableRipple
                            size="small"
                          />
                          <ListItemText primary={column.display_fn(value)} />
                        </MenuItem>
                      ))}
                    </Menu>
                  </div>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {columns.map((column, colIndex) => (
                  <TableCell key={colIndex}>
                    <Typography variant="body1" color="primary.dark">
                      {column.display_fn(row[column.id])}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
